/**
 * 매입 영수증/거래명세서 OCR 텍스트 파서 (순수 함수 — import 없음)
 *
 * 기대 형식: 한 줄에 「품명  수량  단가  금액」 (OCR 이 열 간격을 공백으로 내놓는 표 형태)
 * OCR 오인식은 피할 수 없으므로 결과는 항상 사람이 화면에서 확인·수정한다.
 * 그래서 검산(수량×단가=금액, 합계 대조)이 어긋난 줄에는 warn 을 달아 준다.
 */

const SKIP_LINE = /(품\s*명|수\s*량|단\s*가|합\s*계|공급\s*가|부가\s*세|사업자|전화|tel|거래\s*일|승인|귀하|명\s*세\s*서|영\s*수\s*증|공급자|상\s*호|대표|주소)/i;

const toNum = (s) => {
  const n = Number(String(s).replace(/[^\d]/g, ''));
  return Number.isFinite(n) ? n : 0;
};

/** 거래일자 → 'yyyy-MM-dd' (없으면 '') */
export function parseDate(text) {
  const m = String(text).match(/(20\d{2})\s*[-./년]\s*(\d{1,2})\s*[-./월]\s*(\d{1,2})/);
  if (!m) return '';
  return `${m[1]}-${m[2].padStart(2, '0')}-${m[3].padStart(2, '0')}`;
}

/** 공급자(거래처) 이름 */
export function parseVendor(lines) {
  for (const l of lines) {
    const m = l.match(/(?:공\s*급\s*자|상\s*호|거\s*래\s*처)\s*[:：]?\s*(.+)/);
    if (m && m[1].trim()) return m[1].trim();
  }
  // 못 찾으면 제목 줄이 아닌 첫 줄
  return lines.find((l) => l.length > 1 && !/명\s*세\s*서|영\s*수\s*증/.test(l)) || '';
}

/** 「합계금액」 숫자 (없으면 0) */
export function parseTotal(text) {
  const m = String(text).match(/합\s*계\s*(?:금\s*액)?\s*[:：]?\s*(?:₩|\\)?\s*([\d][\d,. ]*)/);
  return m ? toNum(m[1]) : 0;
}

/** 품목 줄 파싱 */
export function parseLines(lines) {
  const items = [];
  for (const raw of lines) {
    const l = raw.replace(/[|｜]/g, ' ').replace(/\s+/g, ' ').trim();
    if (!l || SKIP_LINE.test(l)) continue;

    // 품명 수량 단가 금액
    let m = l.match(/^(.+?)\s+(\d{1,4})\s+(\d[\d,.]*)\s+(\d[\d,.]*)$/);
    let qty;
    let unitPrice;
    let amount;
    let name;
    if (m) {
      name = m[1];
      qty = toNum(m[2]);
      unitPrice = toNum(m[3]);
      amount = toNum(m[4]);
    } else {
      // 품명 단가 금액 (수량 열 누락) → 금액/단가로 수량 추정
      m = l.match(/^(.+?)\s+(\d[\d,.]{2,})\s+(\d[\d,.]{2,})$/);
      if (!m) continue;
      name = m[1];
      unitPrice = toNum(m[2]);
      amount = toNum(m[3]);
      qty = unitPrice > 0 && amount % unitPrice === 0 ? amount / unitPrice : 1;
    }
    name = name.replace(/^[\d\s.\-*]+(?=\D)/, '').trim(); // 앞쪽 순번 제거
    if (!name || !/[가-힣A-Za-z]/.test(name)) continue;
    items.push({ name, qty, unitPrice, amount, warn: qty * unitPrice !== amount });
  }
  return items;
}

/** 전체 텍스트 → { receiptDate, vendorNm, lines, ocrTotal, sum, totalMismatch } */
export function parseReceipt(text) {
  const lines = String(text || '')
    .split(/\r?\n/)
    .map((s) => s.trim())
    .filter(Boolean);
  const items = parseLines(lines);
  const ocrTotal = parseTotal(text);
  const sum = items.reduce((s, i) => s + i.amount, 0);
  return {
    receiptDate: parseDate(text),
    vendorNm: parseVendor(lines),
    lines: items,
    ocrTotal,
    sum,
    totalMismatch: ocrTotal > 0 && ocrTotal !== sum,
  };
}

/** 정규화 이름(공백·기호 제거, 소문자) */
export const normName = (s) => String(s || '').replace(/[\s()[\]{}.,\-_/·*]/g, '').toLowerCase();

/**
 * 제품마스터 자동 매칭 — 정규화 이름이 같거나 한쪽이 다른 쪽을 포함하면 매칭.
 * @param {string} name OCR 품명
 * @param {{productUid:string, productNm:string}[]} products
 * @returns {string} productUid 또는 ''
 */
export function matchProduct(name, products) {
  const n = normName(name);
  if (!n) return '';
  const exact = products.find((p) => normName(p.productNm) === n);
  if (exact) return exact.productUid;
  const pick = (list) => (list.length === 1 ? list[0].productUid : '');
  const part = pick(products.filter((p) => {
    const pn = normName(p.productNm);
    return pn && (pn.includes(n) || n.includes(pn));
  }));
  if (part) return part;
  // OCR 이 단위(ml·매·입)를 깨뜨리는 경우가 많아, 한글만 남긴 이름으로 한 번 더 비교
  const h = hangulOnly(name);
  if (h.length < 2) return '';
  return pick(products.filter((p) => {
    const ph = hangulOnly(p.productNm);
    return ph && (ph === h || ph.includes(h) || h.includes(ph));
  }));
}

const hangulOnly = (s) => String(s || '').replace(/[^가-힣]/g, '');
